import { useParams, Navigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { slugify } from '../utils/cityServiceAreaData';
import { Button } from '../utils/Button';
import Breadcrumbs from '../components/Breadcrumbs';

// Selected showcase images
const SHOWCASE_IMAGES = {
  kitchen: [
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/kitchen/kitchen15.webp',
      alt: 'Modern kitchen remodel with custom cabinets and island'
    },
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/kitchen/kitchen17.webp',
      alt: 'Luxury kitchen renovation with granite countertops'
    },
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/kitchen/kitchen33.webp',
      alt: 'Contemporary kitchen design with premium finishes'
    }
  ],
  bathroom: [
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/bath/bath11.webp',
      alt: 'Spa-like bathroom remodel with custom shower'
    },
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/bath/bath15.webp',
      alt: 'Modern bathroom renovation with luxury fixtures'
    },
    {
      src: 'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/bath/bath13.webp',
      alt: 'Elegant bathroom design with custom tilework'
    }
  ]
};

export default function CityServiceArea() {
  const { county, city } = useParams();
  const location = useLocation();
  const [cityData, setCityData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch('/updatedData.JSON')
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : data.default;
        // Debug log
        console.log('Loaded data:', arr);
        console.log('Looking for county:', county, 'city:', city);
        const found = arr.find(
          (entry) =>
            slugify(entry.County) === county && slugify(entry.City) === city
        );
        console.log('Found:', found);
        if (found) {
          setCityData(found);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setNotFound(true);
      });
  }, [county, city]);

  if (notFound) {
    return <Navigate to="/service-area" replace />;
  }
  if (!cityData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center animate-pulse">
          <div className="mb-4 w-32 h-8 bg-gray-200 rounded"></div>
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  // --- SEO Structured Data ---
  // Business info (customize as needed)
  const businessName = 'Oak and Stone Remodel';
  const businessUrl = 'https://oakandstoneremodel.com';
  const businessLogo =
    'https://res.cloudinary.com/dwoubdppz/image/upload/v1751597760/oak-and-stone-remodel/logo/logo.svg';
  const businessPhone = cityData.Phone || '';
  const businessCity = cityData.City || '';
  const businessCounty = cityData.County || '';
  const businessState = 'UT';
  const businessCountry = 'US';
  const canonicalUrl =
    cityData.URL || `${businessUrl}/service-area/${county}/${city}`;
  const metaDescription =
    cityData.Description ||
    `Expert home remodeling in ${businessCity}, ${businessCounty} County, Utah. Custom kitchens, bathrooms, and more by Oak and Stone Remodel.`;

  // LocalBusiness JSON-LD
  const localBusinessJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: businessName,
    image: businessLogo,
    address: {
      addressLocality: businessCity,
      addressRegion: businessState,
      addressCountry: businessCountry
    },
    telephone: businessPhone,
    url: canonicalUrl
  };

  // FAQPage JSON-LD (if FAQ exists)
  const faqJsonLd =
    cityData.FAQ && cityData.FAQ.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: cityData.FAQ.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        }
      : null;

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Service Area',
        item: `${businessUrl}/service-area`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${businessCounty} County`,
        item: `${businessUrl}/service-area/${county}`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: businessCity,
        item: canonicalUrl
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${businessCity} Home Remodeling | Oak and Stone Remodel`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Structured Data: LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessJsonLd)}
        </script>
        {/* Structured Data: FAQPage */}
        {faqJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        )}
        {/* Structured Data: BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>

      {/* Hero Section with Breadcrumbs */}
      <div className="relative h-[50vh] w-full flex items-center justify-center">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage: `url('https://res.cloudinary.com/dwoubdppz/image/upload/w_1200,q_auto,f_auto/v1751597871/oak-and-stone-remodel/show/show18.webp')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60" />
        <div className="relative z-10 px-4 mx-auto max-w-4xl text-center text-white">
          <h1 className="mb-3 text-4xl font-bold tracking-wide uppercase drop-shadow-lg md:text-6xl">
            {cityData.City}
          </h1>
          <h2 className="mb-6 text-xl font-medium drop-shadow md:text-3xl">
            {cityData.County} County, Utah
          </h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              to="/contact"
              text="Get Your Free Consultation"
              classname="text-white bg-primary transition-all duration-300 transform hover:bg-primary-hover hover:scale-105"
            />
            <Button
              to="/gallery"
              text="View Our Portfolio"
              classname="text-white bg-gray-700 transition-all duration-300 transform hover:bg-gray-800 hover:scale-105"
            />
          </div>
        </div>
        <Breadcrumbs pathname={location.pathname} />
      </div>

      {/* Main Content */}
      <div className="px-4 py-16 mx-auto max-w-4xl">
        {/* Introduction Section */}
        <div className="mb-12 text-center">
          <h3 className="mb-6 text-3xl font-bold text-gray-900">
            Expert Home Remodeling in {cityData.City}
          </h3>
          <p className="mb-8 text-xl leading-relaxed text-gray-700">
            {cityData.Description}
          </p>
          <div className="flex flex-wrap gap-3 justify-center mb-4">
            {cityData.Category &&
              cityData.Category.split(',').map((cat) => (
                <span
                  key={cat}
                  className="px-4 py-2 text-sm font-medium text-blue-800 bg-blue-100 rounded-full transition-colors hover:bg-blue-200">
                  {cat.trim()}
                </span>
              ))}
          </div>
        </div>

        {/* Portfolio Showcase */}
        <div className="mb-16">
          <h3 className="mb-8 text-2xl font-bold text-center text-gray-900">
            Our Recent Projects in {cityData.County} County
          </h3>

          {/* Kitchen Showcase */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-900">
                Kitchen Remodels
              </h4>
              <Link
                to="/gallery/kitchen-remodels"
                className="font-medium text-blue-700 transition-colors hover:text-blue-900">
                View Kitchen Gallery →
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {SHOWCASE_IMAGES.kitchen.map((img, idx) => (
                <Link
                  key={idx}
                  to="/gallery/kitchen-remodels"
                  className="block overflow-hidden relative bg-gray-100 rounded-lg shadow-md transition-all duration-300 group hover:shadow-xl">
                  <div className="relative w-full pt-[75%]">
                    {' '}
                    {/* 4:3 aspect ratio */}
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="object-cover absolute inset-0 w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 from-black/60 via-black/30 group-hover:opacity-100">
                      <div className="absolute right-0 bottom-0 left-0 p-4">
                        <span className="text-sm font-medium text-white">
                          View More Kitchen Projects
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Bathroom Showcase */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-900">
                Bathroom Transformations
              </h4>
              <Link
                to="/gallery/bathroom-remodels"
                className="font-medium text-blue-700 transition-colors hover:text-blue-900">
                View Bathroom Gallery →
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {SHOWCASE_IMAGES.bathroom.map((img, idx) => (
                <Link
                  key={idx}
                  to="/gallery/bathroom-remodels"
                  className="block overflow-hidden relative bg-gray-100 rounded-lg shadow-md transition-all duration-300 group hover:shadow-xl">
                  <div className="relative w-full pt-[75%]">
                    {' '}
                    {/* 4:3 aspect ratio */}
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="object-cover absolute inset-0 w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 from-black/60 via-black/30 group-hover:opacity-100">
                      <div className="absolute right-0 bottom-0 left-0 p-4">
                        <span className="text-sm font-medium text-white">
                          View More Bathroom Projects
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Button
              to="/gallery/showcase"
              text="Explore Our Full Portfolio"
              classname="text-white bg-primary transition-all duration-300 transform hover:bg-primary-hover hover:scale-105"
            />
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid gap-8 mb-16 md:grid-cols-2">
          {cityData.Landmarks && (
            <div className="p-6 bg-gray-50 rounded-lg shadow-sm transition-shadow hover:shadow-md">
              <h4 className="mb-3 text-xl font-semibold text-gray-900">
                Local Landmarks
              </h4>
              <p className="leading-relaxed text-gray-700">
                {cityData.Landmarks}
              </p>
            </div>
          )}
          {cityData.Specialties && (
            <div className="p-6 bg-gray-50 rounded-lg shadow-sm transition-shadow hover:shadow-md">
              <h4 className="mb-3 text-xl font-semibold text-gray-900">
                Our Specialties
              </h4>
              <p className="leading-relaxed text-gray-700">
                {cityData.Specialties}
              </p>
              <Link
                to="/gallery"
                className="inline-block mt-4 font-medium text-blue-700 transition-colors hover:text-blue-900">
                See Our Work →
              </Link>
            </div>
          )}
          {cityData.ServiceRadius && (
            <div className="p-6 bg-gray-50 rounded-lg shadow-sm transition-shadow hover:shadow-md">
              <h4 className="mb-3 text-xl font-semibold text-gray-900">
                Service Area
              </h4>
              <p className="leading-relaxed text-gray-700">
                {cityData.ServiceRadius}
              </p>
            </div>
          )}
          {cityData.CallToAction && (
            <div className="p-6 bg-blue-50 rounded-lg shadow-sm transition-shadow hover:shadow-md">
              <h4 className="mb-3 text-xl font-semibold text-blue-900">
                Ready to Start?
              </h4>
              <p className="mb-4 font-medium leading-relaxed text-blue-800">
                {cityData.CallToAction}
              </p>
              <div className="flex gap-4">
                <Button
                  to="/contact"
                  text="Contact Us"
                  classname="text-white bg-primary transition-all duration-300 transform hover:bg-primary-hover hover:scale-105"
                />
                <Button
                  to="/gallery"
                  text="View Portfolio"
                  classname="text-blue-700 bg-white border border-blue-700 transition-all duration-300 transform hover:bg-blue-50 hover:scale-105"
                />
              </div>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        {cityData.FAQ && cityData.FAQ.length > 0 && (
          <div className="mb-16">
            <h4 className="mb-8 text-2xl font-bold text-center text-gray-900">
              Frequently Asked Questions
            </h4>
            <div className="space-y-6">
              {cityData.FAQ.map((faq, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-white rounded-lg shadow-sm transition-all duration-300 hover:shadow-md">
                  <h5 className="mb-3 text-lg font-semibold text-gray-900">
                    {faq.question}
                  </h5>
                  <p className="leading-relaxed text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div className="text-center">
          <div className="mb-8">
            <a
              href={`tel:${
                cityData.Phone ? cityData.Phone.replace(/[^0-9]/g, '') : ''
              }`}
              className="inline-block mb-4 text-2xl font-bold text-blue-700 transition-colors hover:text-blue-800">
              {cityData.Phone}
            </a>
            <p className="mb-6 text-gray-600">
              Serving ZIP code: {cityData['ZIP Code']}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              to="/contact"
              text="Schedule Your Free Consultation"
              classname="text-white bg-primary transition-all duration-300 transform hover:bg-primary-hover hover:scale-105"
            />
            <Button
              to="/gallery"
              text="Browse Our Portfolio"
              classname="text-blue-700 bg-white border border-blue-700 transition-all duration-300 transform hover:bg-blue-50 hover:scale-105"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
